import { useCallback, useMemo, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';

export interface UnsavedDraft {
  setChanged: (changed: boolean) => void;
  leave: (go: () => void) => void;
}

/** Holds every way off an engagement behind "Discard unsaved changes?" while a typed-in draft is unsaved. */
export const useUnsavedDraftGuard = () => {
  const changed = useRef(false);
  const [waiting, setWaiting] = useState<{ go: () => void } | null>(null);

  const setChanged = useCallback((next: boolean) => {
    changed.current = next;
  }, []);

  const leave = useCallback((go: () => void) => {
    if (changed.current) setWaiting({ go });
    else go();
  }, []);

  useBeforeUnload(
    useCallback((event: BeforeUnloadEvent) => {
      if (changed.current) event.preventDefault();
    }, [])
  );

  const unsavedDraft = useMemo<UnsavedDraft>(() => ({ setChanged, leave }), [setChanged, leave]);
  const discardDialog = {
    open: !!waiting,
    onDiscard: () => {
      setWaiting(null);
      waiting?.go();
    },
    onKeep: () => setWaiting(null)
  };
  return { unsavedDraft, discardDialog };
};
